<template>
  <div class="mt-4 flex flex-col items-center justify-center gap-4">
    <p v-if="sla.expired">0:00</p>
    <p v-else>{{ `${displayTime}` }} minuter kvar.</p>

    <div
      v-if="lessThanOneMinuteRemaining"
      class="mb-12 flex flex-col items-center justify-center gap-2"
    >
      <Button
        v-if="canExtendTime"
        title="Förläng signeringstiden"
        :disabled="!canExtendTime"
        @click="updateSla"
      />
      <p class="text-sm">Du kan förlänga signeringstiden högst 10 gånger.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import moment from 'moment'
import { computed, inject, onBeforeUnmount, onMounted, ref, type Ref } from 'vue'
import Button from './Button.vue'

const sla = inject<Ref<{ time: number; expired: boolean }>>('sla', ref({ time: 5, expired: false }))
const updateCountdown = inject<Ref<number>>('updateCountdown', ref(0))

const intervalId = ref<NodeJS.Timeout | null>(null)
const displayTime = ref('')

// Logic to check if the user can extend the time
// Can extend if the SLA is not expired and the countdown has been reset less than 10 times
// and there is less than 1 minute remaining
const maxUpdatesPerformed = computed(() => {
  return updateCountdown.value < 10
})
const lessThanOneMinuteRemaining = computed(() => {
  const [minutes, seconds] = displayTime.value.split(':').map(Number)
  const totalSecondsRemaining = minutes * 60 + seconds
  return totalSecondsRemaining > 0 && totalSecondsRemaining <= 60
})
const canExtendTime = computed(
  () => maxUpdatesPerformed.value && lessThanOneMinuteRemaining.value && !sla.value.expired,
)

const updateSla = () => {
  if (canExtendTime.value) {
    sla.value.time = 5
    updateCountdown.value++

    if (intervalId.value) {
      clearInterval(intervalId.value)
    }
    if (!sla.value.expired) {
      startCountdown()
    }
  }
}

const startCountdown = () => {
  const initialDate = moment().add(sla.value.time, 'minutes')

  intervalId.value = setInterval(() => {
    const countDownDuration = initialDate.diff(moment())
    let secondsRemaining = moment.duration(countDownDuration).asSeconds()

    if (secondsRemaining <= 0) {
      clearInterval(intervalId.value!)
      sla.value.expired = true
      displayTime.value = '0:00'
      return
    }

    const minutes = Math.floor((secondsRemaining % 3600) / 60)
    const seconds = Math.floor(secondsRemaining % 60)

    displayTime.value = `${minutes}:${seconds.toString().padStart(2, '0')}`
  }, 1000)
}

onMounted(() => {
  if (!sla.value.expired) {
    startCountdown()
  }
})
onBeforeUnmount(() => {
  if (intervalId.value) {
    clearInterval(intervalId.value)
  }
})
</script>
